import * as XLSX from 'xlsx';
import { normalizeQuarterlyActuals } from './utils';

const calculateIkrProgressForExport = (ikr) => {
  const actuals = normalizeQuarterlyActuals(ikr.quarterlyActuals);
  const target = parseFloat(ikr.kpiTarget) || 0;
  const method = ikr.calculationMethod || 'sum';

  if (target <= 0) return { effectiveActual: 0, percentComplete: 0 };

  if (method === 'average') {
    const filledActuals = actuals.filter(val => val !== null && val !== undefined && val !== '');
    if (filledActuals.length === 0) return { effectiveActual: 0, percentComplete: 0 };
    const avg = filledActuals.reduce((sum, val) => sum + (parseFloat(val) || 0), 0) / filledActuals.length;
    return { effectiveActual: avg.toFixed(2), percentComplete: ((avg / target) * 100).toFixed(1) };
  } else if (method === 'deviation') {
    let latestActual = null;
    for (let i = actuals.length - 1; i >= 0; i--) {
      if (actuals[i] !== null && actuals[i] !== undefined && actuals[i] !== '') {
        latestActual = parseFloat(actuals[i]) || 0;
        break;
      }
    }
    if (latestActual === null) return { effectiveActual: 0, percentComplete: 0 };
    if (latestActual === 0) return { effectiveActual: 0, percentComplete: target > 0 ? 200 : 0 };
    const progress = (target / latestActual) * 100;
    return { effectiveActual: latestActual, percentComplete: progress.toFixed(1) };
  } else {
    const total = actuals.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    return { effectiveActual: total, percentComplete: ((total / target) * 100).toFixed(1) };
  }
};

export const exportOKRsToExcel = (okrs, users, teams) => {
  const data = [];
  okrs.forEach(okr => {
    const teamName = teams[okr.primaryTeamId]?.name || 'Unknown Team';
    okr.keyResults?.forEach(kr => {
      kr.individualKRs?.forEach(ikr => {
        const owner = users.find(u => u.uid === ikr.ownerId || u.id === ikr.ownerId);
        const ownerName = owner?.name || 'Unassigned';
        const ownerTeam = teams[ikr.teamId]?.name || teams[owner?.teamId]?.name || 'Unknown';
        const actuals = normalizeQuarterlyActuals(ikr.quarterlyActuals);
        const method = ikr.calculationMethod || 'sum';
        const { effectiveActual, percentComplete } = calculateIkrProgressForExport(ikr);
        data.push({
          'Objective': okr.objective,
          'Primary Team': teamName,
          'Team Key Result': kr.text,
          'Individual Key Result': ikr.text,
          'Assigned To': ownerName,
          'Owner Team': ownerTeam,
          'KPI Target': parseFloat(ikr.kpiTarget) || 0,
          'Calculation Method': method === 'average' ? 'Average' : 'Sum',
          'Q1 Actual': actuals[0] ?? '',
          'Q2 Actual': actuals[1] ?? '',
          'Q3 Actual': actuals[2] ?? '',
          'Q4 Actual': actuals[3] ?? '',
          'Effective Actual': effectiveActual,
          '% Complete': `${percentComplete}%`
        });
      });
    });
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'All OKRs');
  const colWidths = Object.keys(data[0] || {}).map(key => ({
    wch: Math.max(key.length, ...data.map(row => String(row[key] || '').length)) + 2
  }));
  ws['!cols'] = colWidths;
  XLSX.writeFile(wb, `OKRs_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
};

/** Widest cell in a column, without spreading a big array into Math.max. */
const columnWidth = (rows, key) => ({
  wch: Math.min(rows.reduce((w, row) => Math.max(w, String(row[key] ?? '').length), key.length) + 2, 50),
});

/** Run `worker` over `items` at most `limit` at a time. */
const mapWithLimit = async (items, limit, worker) => {
  const out = [];
  for (let i = 0; i < items.length; i += limit) {
    out.push(...await Promise.all(items.slice(i, i + limit).map(worker)));
  }
  return out;
};

/**
 * How many people to read concurrently. Each one costs three document reads, so
 * this is ~36 requests in flight — comfortably within what Firestore and the
 * browser will pipeline, and enough to stop latency dominating the run.
 */
const EXPORT_CONCURRENCY = 12;

/**
 * Export evaluations, one sheet per period.
 *
 * Reads are issued concurrently and in bounded batches. Done one-at-a-time this
 * is 5 periods x every user x 3 documents of strictly serial round-trips — about
 * 2,400 for a 160-person roster, which took minutes and looked like a hang.
 *
 * @param {Function} [onProgress] called with (done, total) so the UI can report progress
 */
export const exportEvaluationsToExcel = async (users, okrs, db, collection, getDocs, fy = 'FY2526', okrsFY2627 = [], onProgress) => {
  const { getDoc, doc } = await import('firebase/firestore');
  const wb = XLSX.utils.book_new();
  const quarters = ['Q1', 'Q2', 'Q3', 'Q4', 'Annual'];
  const evalSubcollection = fy === 'FY2627' ? 'evaluations_fy2627' : 'evaluations';

  const people = (users || []).filter(u => u.uid || u.id);
  const totalSteps = quarters.length * people.length;
  const progress = { done: 0 };

  for (const quarter of quarters) {
    const rowGroups = await mapWithLimit(people, EXPORT_CONCURRENCY, async (user) => {
      const quarterData = [];
      const userId = user.uid || user.id;
      try {
        const userIKRs = [];
        if (fy === 'FY2627') {
          okrsFY2627.forEach(obj => {
            obj.keyResults?.forEach(kr => {
              kr.teamKRs?.forEach(tkr => {
                tkr.individualKRs?.forEach(ikr => {
                  if (ikr.ownerId === userId) {
                    // FY26-27 IKRs are owner threads: the text and target live on
                    // yearlyText/yearlyTarget and the actuals inside quarters[].
                    // Reading the FY25-26 field names here yielded a blank IKR and a
                    // zero target on every row. Legacy flat rows may still coexist,
                    // so both shapes are handled.
                    const isThread = Array.isArray(ikr.quarters);
                    userIKRs.push({
                      ikrId: ikr.id,
                      ikrText: (isThread ? ikr.yearlyText : ikr.text) || '',
                      teamKrText: tkr.text,
                      objectiveText: `${obj.code}: ${obj.objective}`,
                      krCode: kr.code,
                      kpiTarget: isThread ? ikr.yearlyTarget : ikr.kpiTarget,
                      quarterlyActuals: isThread
                        ? ikr.quarters.map(q => (q ? q.actual : null))
                        : ikr.quarterlyActuals,
                    });
                  }
                });
              });
            });
          });
        } else {
          okrs.forEach(okr => {
            okr.keyResults?.forEach(tkr => {
              tkr.individualKRs?.forEach(ikr => {
                if (ikr.ownerId === userId) {
                  userIKRs.push({
                    ikrId: ikr.id, ikrText: ikr.text, teamKrText: tkr.text,
                    objectiveText: okr.objective, kpiTarget: ikr.kpiTarget,
                    quarterlyActuals: ikr.quarterlyActuals
                  });
                }
              });
            });
          });
        }
        // All three are independent, so fetch them together rather than in series.
        const base = `users/${userId}/${evalSubcollection}/${quarter}`;
        const [evaluationsSnap, statusSnap, npsSnap] = await Promise.all([
          getDocs(collection(db, `${base}/ikrs`)),
          getDoc(doc(db, `${base}/status/current`)),
          getDoc(doc(db, `${base}/cards/npsScore`)),
        ]);
        const evaluations = {};
        evaluationsSnap.forEach(docSnap => { evaluations[docSnap.id] = docSnap.data(); });
        const status = statusSnap.exists() ? statusSnap.data().status : 'pending';
        const npsScore = npsSnap.exists() ? npsSnap.data().content : null;

        if (userIKRs.length > 0) {
          userIKRs.forEach(ikr => {
            const evaluation = evaluations[ikr.ikrId] || {};
            const actuals = normalizeQuarterlyActuals(ikr.quarterlyActuals);
            const target = parseFloat(ikr.kpiTarget) || 0;
            let periodActual, periodLabel;
            if (quarter === 'Annual') {
              periodActual = actuals.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
              periodLabel = 'Annual Actual';
            } else {
              const quarterIndex = parseInt(quarter.replace('Q', ''), 10) - 1;
              periodActual = actuals[quarterIndex] ?? 0;
              periodLabel = `${quarter} Actual`;
            }
            const progress = target > 0 ? ((periodActual / target) * 100).toFixed(1) : 0;
            quarterData.push({
              'Employee Name': user.name,
              'Employee Email': user.email,
              'Team': user.teamId,
              'Role': user.role,
              'Evaluation Status': status,
              'NPS Score (1-10)': (npsScore !== null && npsScore !== undefined && npsScore !== '') ? npsScore : '',
              'Objective': ikr.objectiveText,
              'Team Key Result': ikr.teamKrText,
              'Individual Key Result': ikr.ikrText,
              'KPI Target': target,
              [periodLabel]: periodActual,
              '% Progress': `${progress}%`,
              'Self Eval - What Went Well': evaluation.selfEvaluation?.wentWell || '',
              'Self Eval - What Could Be Better': evaluation.selfEvaluation?.couldBeBetter || '',
              'Self Eval - Next Focus': evaluation.selfEvaluation?.nextFocus || '',
              'Manager Name': evaluation.managerFeedback?.managerName || '',
              'Manager Rating': evaluation.managerFeedback?.rating || '',
              'Manager Feedback': evaluation.managerFeedback?.feedback || ''
            });
          });
        } else if (npsScore !== null && npsScore !== undefined) {
          quarterData.push({
            'Employee Name': user.name,
            'Employee Email': user.email,
            'Team': user.teamId,
            'Role': user.role,
            'Evaluation Status': status,
            'NPS Score (1-10)': npsScore,
            'Objective': '', 'Team Key Result': '', 'Individual Key Result': '', 'KPI Target': '',
            [quarter === 'Annual' ? 'Annual Actual' : `${quarter} Actual`]: '',
            '% Progress': '',
            'Self Eval - What Went Well': '', 'Self Eval - What Could Be Better': '', 'Self Eval - Next Focus': '',
            'Manager Name': '', 'Manager Rating': '', 'Manager Feedback': ''
          });
        }
      } catch (error) {
        // One unreadable person must not sink the whole export.
        console.error(`Error fetching evaluations for user ${userId}:`, error);
      }
      progress.done += 1;
      if (onProgress) onProgress(progress.done, totalSteps);
      return quarterData;
    });

    const quarterData = rowGroups.flat();
    if (quarterData.length > 0) {
      const ws = XLSX.utils.json_to_sheet(quarterData);
      ws['!cols'] = Object.keys(quarterData[0] || {}).map(key => columnWidth(quarterData, key));
      XLSX.utils.book_append_sheet(wb, ws, quarter);
    } else {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([]), quarter);
    }
  }
  XLSX.writeFile(wb, `Evaluations_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
};

/**
 * Export the employee roster from the Employee Management tab.
 *
 * Two sheets: the roster itself, and a Data Issues sheet listing the broken
 * manager links the tab flags inline. The second one is the useful half — it
 * turns warnings scattered across ~160 expandable rows into one worklist.
 *
 * @param {Array}  users        user documents ({ id, ...data })
 * @param {Object} teams        teams keyed by doc id
 * @param {Map}    krCodeIndex  from buildKrCodeIndex, to resolve accessibleKRs
 */
export const exportEmployeesToExcel = (users, teams, krCodeIndex) => {
  const byEmail = new Map((users || [])
    .filter(u => u.email)
    .map(u => [u.email.toLowerCase(), u]));

  const roster = [];
  const issues = [];

  (users || []).forEach(user => {
    const manager = user.managerEmail ? byEmail.get(user.managerEmail.toLowerCase()) : null;
    const codes = [...new Set((user.accessibleKRs || [])
      .map(c => String(c || '').trim())
      .filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const unknownCodes = codes.filter(c => !krCodeIndex?.get(c));

    roster.push({
      'Name': user.name || '',
      'Email': user.email || '',
      'Role': user.role || '',
      'Team': teams?.[user.teamId]?.name || user.team || '',
      'Manager Email': user.managerEmail || '',
      'Manager Name': manager?.name || '',
      'Manager Linked By ID': user.managerId ? 'Yes' : 'No',
      'Direct Reports': (user.directReports || []).length,
      'FY 26-27 KR Access': codes.join(', '),
      'KR Access Count': codes.length,
      'User ID': user.id || '',
    });

    // Mirrors the warnings EmployeeDetailPanel shows, so the sheet and the UI
    // never disagree about what counts as a problem.
    const flag = (issue, detail) => issues.push({
      'Name': user.name || '', 'Email': user.email || '', 'Issue': issue, 'Detail': detail,
    });

    if (user.managerEmail && !manager) {
      flag('Manager not in database', `${user.managerEmail} has no user record — nobody can review their evaluations`);
    } else if (user.managerEmail && !user.managerId) {
      flag('Manager not linked by ID', 'Firestore rules match a manager by managerId, so their manager cannot open their evaluations');
    }
    if (manager && Array.isArray(manager.directReports) && manager.directReports.length > 0
        && !manager.directReports.includes(user.id)) {
      flag('Missing from manager\'s direct reports', `Will not appear in ${manager.name || manager.email}'s evaluation list`);
    }
    if (!user.managerEmail) flag('No manager set', 'No manager email on the profile');
    if (unknownCodes.length) {
      flag('Stale KR access', `${unknownCodes.join(', ')} no longer exist in FY 26-27`);
    }
  });

  const wb = XLSX.utils.book_new();
  const autoWidth = (rows) => Object.keys(rows[0] || {}).map(key => ({
    wch: Math.max(key.length, ...rows.map(row => String(row[key] ?? '').length)) + 2,
  }));

  const rosterSheet = XLSX.utils.json_to_sheet(roster);
  rosterSheet['!cols'] = autoWidth(roster);
  XLSX.utils.book_append_sheet(wb, rosterSheet, 'Employees');

  const issuesSheet = XLSX.utils.json_to_sheet(
    issues.length ? issues : [{ Name: '', Email: '', Issue: 'No data issues found', Detail: '' }]
  );
  issuesSheet['!cols'] = autoWidth(issues.length ? issues : [{ Name: '', Email: '', Issue: 'x', Detail: '' }]);
  XLSX.utils.book_append_sheet(wb, issuesSheet, 'Data Issues');

  XLSX.writeFile(wb, `Employees_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
  return { employees: roster.length, issues: issues.length };
};
